import * as THREE from 'three';

// STONE SURFACE DETAIL for rocks + ore boulders: strata bands on vertical faces, bright mineral
// speckle, and a soft underside/crevice AO -- all world-space value noise in the fragment shader
// (never tiles, no textures). Instancing-aware. Composes with the material's existing
// onBeforeCompile chain (interior-darken, cloud shadows) the same way applyCloudShadow does:
// each wrapper calls the previous and replaces its own distinct anchor.

const STONE_GLSL = /* glsl */ `
// precision-safe: wrapped lattice + sin-free hash (large world coords quantize the old fract(p*k) form)
float oxStHash(vec2 p){ p = mod(p, 1024.0); vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
float oxStNoise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = oxStHash(i), b = oxStHash(i + vec2(1.0, 0.0)), c = oxStHash(i + vec2(0.0, 1.0)), d = oxStHash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}`;

export function applyStoneDetail(mat: THREE.Material, key: string): void {
  const prevCompile = mat.onBeforeCompile;
  const prevKey = mat.customProgramCacheKey;
  mat.onBeforeCompile = (shader, renderer) => {
    prevCompile.call(mat, shader, renderer);
    shader.vertexShader = 'varying vec3 vStoneWP;\nvarying vec3 vStoneN;\n' + shader.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
       vec4 oxSWP = vec4(transformed, 1.0);
       #ifdef USE_INSTANCING
         oxSWP = instanceMatrix * oxSWP;
       #endif
       vStoneWP = (modelMatrix * oxSWP).xyz;
       vec3 oxSN = objectNormal;
       #ifdef USE_INSTANCING
         oxSN = mat3(instanceMatrix) * oxSN;
       #endif
       vStoneN = normalize(mat3(modelMatrix) * oxSN);`,
    );
    shader.fragmentShader = ('varying vec3 vStoneWP;\nvarying vec3 vStoneN;\n' + STONE_GLSL + '\n' + shader.fragmentShader).replace(
      '#include <color_fragment>',
      `#include <color_fragment>
      {
        float side = 1.0 - abs(vStoneN.y);                       // 1 on vertical faces, 0 on tops
        // Strata: horizontal weather bands wandering with a little noise -- bedded stone, strongest on the sides.
        float band = 0.5 + 0.5 * sin(vStoneWP.y * 3.1 + oxStNoise(vStoneWP.xz * 0.8) * 3.0);
        diffuseColor.rgb *= 0.92 + 0.14 * band * side;
        // Mineral speckle: sparse bright grit flecks (two offset fields so they read scattered, not gridded).
        float spk = oxStNoise(vStoneWP.xz * 9.0 + vStoneWP.y * 7.0);
        diffuseColor.rgb += vec3(0.05, 0.048, 0.04) * smoothstep(0.82, 0.97, spk);
        // Grain mottle + a soft crevice/underside AO (undersides read tucked-in, tops catch light).
        diffuseColor.rgb *= 0.93 + 0.12 * oxStNoise(vStoneWP.xz * 2.1 + vStoneWP.y * 1.7);
        diffuseColor.rgb *= 0.82 + 0.18 * clamp(vStoneN.y * 0.5 + 0.5, 0.0, 1.0);
      }`,
    );
  };
  mat.customProgramCacheKey = () => prevKey.call(mat) + '|stonedetail1-' + key;
}
